import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import BookingFilters from './components/BookingFilters';
import BookingTableRow from './components/BookingTableRow';
import BookingMobileCard from './components/BookingMobileCard';
import BookingDetailsModal from './components/BookingDetailsModal';
import BulkActionsBar from './components/BulkActionsBar';
import EmptyBookingsState from './components/EmptyBookingsState';

import Icon from '../../components/AppIcon';
import Pagination from '../../components/ui/Pagination';
import ExportReportModal from '../../components/ui/ExportReportModal';
import { generateBookingsReport } from '../../utils/reportGenerator';
import { downloadTextFile, escapeCsvValue, openPrintableReport } from '../../utils/exportUtils';
import repositoryContainer from '../../services/RepositoryContainer';
import { useNotifications } from '../../components/context/NotificationContext';
import { useAuth } from '../../components/context/AuthContext';
import { computeBookingDateParams } from '../../utils/managementDateFilters';



const BookingsManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: 'all',
    dateRange: 'upcoming',
    service: 'all',
    startDate: '',
    endDate: ''
  });

  // data loading state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const { recordRefreshVersion, newBookingCount, clearNewBookingCount } = useNotifications();
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');

  
  const [isRestoring, setIsRestoring] = useState(false);

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      await repositoryContainer.company.restoreBusiness();
      // Force a reload to refresh the user context and token from the backend
      window.location.reload(); 
    } catch (err) {
      console.error('Failed to restore business:', err);
      alert('Could not restore the business. Please contact support.');
      setIsRestoring(false);
    }
  };

if (user?.company && user.company.isActive === false) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;700&display=swap');

        .archive-container {
          font-family: 'DM Sans', sans-serif;
          color: #fff;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          background: #000;
        }

        .bg-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://weyyfslmfqjlkvtfwunm.supabase.co/storage/v1/object/public/Claw/Herosection/heroImage.png');
          background-size: cover;
          background-position: center;
          filter: blur(15px) brightness(0.4);
          opacity: 0.6;
          z-index: 0;
        }

        .archive-card {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 480px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 48px 32px;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .status-icon-wrap {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .archive-badge {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.3);
          color: #eab308;
          border-radius: 99px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 20px;
        }

        .archive-title {
          font-family: 'DM Serif Display', serif;
          font-size: 32px;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .archive-desc {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
          margin-bottom: 32px;
          font-weight: 300;
        }

        .info-row {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          padding: 16px;
          text-align: left;
          margin-bottom: 32px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .info-item {
          display: flex;
          gap: 12px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 8px;
        }

        .restore-btn {
          width: 100%;
          background: #fff;
          color: #000;
          border: none;
          padding: 16px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 12px;
        }

        .restore-btn:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }

        .signout-btn {
          width: 100%;
          background: transparent;
          color: rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 16px;
          border-radius: 12px;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .signout-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
        }
      `}</style>

      <div className="archive-container">
        <div className="bg-overlay" />
        
        <div className="archive-card">
          <div className="status-icon-wrap">
            <Icon name="Archive" size={32} color="rgba(255,255,255,0.7)" />
          </div>

          <div className="archive-badge">Status: Archived</div>
          
          <h1 className="archive-title">Workspace Offline</h1>
          <p className="archive-desc">
            Your business profile for <strong>{user?.company?.name || 'your store'}</strong> has been deactivated. All public booking services and dashboard tools are currently restricted.
          </p>

          <div className="info-row">
            <div className="info-item">
              <Icon name="EyeOff" size={16} color="#eab308" />
              <span>Public booking page is hidden</span>
            </div>
            <div className="info-item">
              <Icon name="Database" size={16} color="#eab308" />
              <span>Data is preserved for compliance</span>
            </div>
            <div className="info-item">
              <Icon name="HelpCircle" size={16} color="#eab308" />
              <span>Contact support@clawappz.com</span>
            </div>
          </div>

          <button className="restore-btn" onClick={handleRestore} disabled={isRestoring}>
            {isRestoring ? 'Processing Restoration...' : 'Reactivate Business'}
          </button>
          
          <button className="signout-btn" onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    </>
  );
}

  // fallback/mock data in case API is unavailable
  const mockBookings = [
    // {
    //   id: 1001,
    //   clientName: "Sarah Johnson",
    //   clientEmail: "sarah.johnson@email.com",
    //   clientPhone: "+1 (555) 123-4567",
    //   serviceName: "Classic Manicure",
    //   date: "2025-01-05",
    //   time: "10:00",
    //   status: "confirmed",
    //   notes: "Prefers light pink polish. Regular client.",
    //   history: [
    //     { action: "Booking confirmed", timestamp: "2024-12-28T14:30:00" },
    //     { action: "Booking created", timestamp: "2024-12-28T14:25:00" }
    //   ]
    // },
    // {
    //   id: 1002,
    //   clientName: "Emily Rodriguez",
    //   clientEmail: "emily.r@email.com",
    //   clientPhone: "+1 (555) 234-5678",
    //   serviceName: "Gel Nails with Art",
    //   date: "2025-01-05",
    //   time: "14:30",
    //   status: "pending",
    //   notes: "First-time client. Interested in floral designs.",
    //   history: [
    //     { action: "Booking created", timestamp: "2024-12-30T09:15:00" }
    //   ]
    // },
    // {
    //   id: 1003,
    //   clientName: "Jessica Martinez",
    //   clientEmail: "jess.martinez@email.com",
    //   clientPhone: "+1 (555) 345-6789",
    //   serviceName: "Spa Pedicure",
    //   date: "2025-01-06",
    //   time: "11:00",
    //   status: "confirmed",
    //   notes: "Allergic to lavender scents. Use unscented products.",
    //   history: [
    //     { action: "Booking confirmed", timestamp: "2024-12-29T16:45:00" },
    //     { action: "Booking created", timestamp: "2024-12-29T16:40:00" }
    //   ]
    // },
    // {
    //   id: 1004,
    //   clientName: "Amanda Chen",
    //   clientEmail: "amanda.chen@email.com",
    //   clientPhone: "+1 (555) 456-7890",
    //   serviceName: "Acrylic Full Set",
    //   date: "2025-01-07",
    //   time: "13:00",
    //   status: "confirmed",
    //   notes: "Prefers square shape, medium length.",
    //   history: [
    //     { action: "Booking confirmed", timestamp: "2024-12-30T11:20:00" },
    //     { action: "Booking created", timestamp: "2024-12-30T11:15:00" }
    //   ]
    // },
    // {
    //   id: 1005,
    //   clientName: "Rachel Thompson",
    //   clientEmail: "rachel.t@email.com",
    //   clientPhone: "+1 (555) 567-8901",
    //   serviceName: "Nail Art Design",
    //   date: "2025-01-08",
    //   time: "15:30",
    //   status: "pending",
    //   notes: "Wants ombre effect with glitter accent.",
    //   history: [
    //     { action: "Booking created", timestamp: "2024-12-31T08:30:00" }
    //   ]
    // },
    // {
    //   id: 1006,
    //   clientName: "Michelle Davis",
    //   clientEmail: "michelle.davis@email.com",
    //   clientPhone: "+1 (555) 678-9012",
    //   serviceName: "Classic Manicure",
    //   date: "2024-12-30",
    //   time: "10:00",
    //   status: "completed",
    //   notes: "Regular weekly appointment.",
    //   history: [
    //     { action: "Booking completed", timestamp: "2024-12-30T11:00:00" },
    //     { action: "Booking confirmed", timestamp: "2024-12-27T14:00:00" },
    //     { action: "Booking created", timestamp: "2024-12-27T13:55:00" }
    //   ]
    // },
    // {
    //   id: 1007,
    //   clientName: "Lisa Anderson",
    //   clientEmail: "lisa.anderson@email.com",
    //   clientPhone: "+1 (555) 789-0123",
    //   serviceName: "Gel Polish",
    //   date: "2024-12-29",
    //   time: "14:00",
    //   status: "completed",
    //   notes: "Chose coral color. Very satisfied with service.",
    //   history: [
    //     { action: "Booking completed", timestamp: "2024-12-29T15:15:00" },
    //     { action: "Booking confirmed", timestamp: "2024-12-26T10:30:00" },
    //     { action: "Booking created", timestamp: "2024-12-26T10:25:00" }
    //   ]
    // },
    // {
    //   id: 1008,
    //   clientName: "Jennifer Wilson",
    //   clientEmail: "jen.wilson@email.com",
    //   clientPhone: "+1 (555) 890-1234",
    //   serviceName: "Spa Pedicure",
    //   date: "2024-12-28",
    //   time: "16:00",
    //   status: "cancelled",
    //   notes: "Client cancelled due to scheduling conflict.",
    //   history: [
    //     { action: "Booking cancelled", timestamp: "2024-12-28T09:00:00" },
    //     { action: "Booking confirmed", timestamp: "2024-12-25T15:20:00" },
    //     { action: "Booking created", timestamp: "2024-12-25T15:15:00" }
    //   ]
    // }
  ];


  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);

  const handleLogout = async () => {
    await logout();
  };

const PAGE_SIZE = 20;
const [page, setPage] = useState(1);
const [totalCount, setTotalCount] = useState(0);
const [totalPages, setTotalPages] = useState(0);

const updateBookingStatus = async (id, status, cancellationReason = null) => {
  try {
    return await repositoryContainer.booking.updateStatus(id, status, cancellationReason);
  } catch (error) {
    console.error('Failed to update booking status:', error);
  }
};

const bulkUpdateBookingStatus = async (ids, status, cancellationReason = null) => {
  try {
    return await repositoryContainer.booking.bulkUpdateStatus(ids, status, cancellationReason);
  } catch (error) {
    console.error('Failed to bulk update booking statuses:', error);
  }
};

const loadBookings = async (pageNum = 1) => {
  setLoading(true);
  setError(null);
  const { startDate, endDate } = computeBookingDateParams(filters);
  try {
    const raw = await repositoryContainer.booking.listPaged({
      page: pageNum,
      pageSize: PAGE_SIZE,
      status: filters.status !== 'all' ? filters.status : undefined,
      startDate,
      endDate,
    });
    const data = raw?.response ?? raw;
    setBookings(data.items ?? []);
    setSelectedBooking((current) => {
      if (!current) return current;
      const refreshed = (data.items ?? []).find((booking) => booking.id === current.id);
      return refreshed ? { ...current, ...refreshed } : current;
    });
    setTotalCount(data.totalCount ?? 0);
    setTotalPages(data.totalPages ?? 0);
    setPage(pageNum);
  } catch (err) {
    setError(err);
    setBookings([]);
  } finally {
    setLoading(false);
  }
};

const handlePageChange = (p) => loadBookings(p);

useEffect(() => {
  loadBookings(1);
}, [filters.status, filters.dateRange, filters.startDate, filters.endDate]);

useEffect(() => {
  if (recordRefreshVersion > 0) loadBookings(page);
}, [recordRefreshVersion]);

useEffect(() => {
  if (newBookingCount > 0) clearNewBookingCount();
}, [clearNewBookingCount, newBookingCount]);


  useEffect(() => {
    filterAndSortBookings();
  }, [filters, sortConfig, bookings]);

  const filterAndSortBookings = () => {
    let filtered = [...bookings];

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b?.clientName?.toLowerCase()?.includes(query) ||
        b?.clientEmail?.toLowerCase()?.includes(query) ||
        b?.clientPhone?.includes(query)
      );
    }

    if (filters?.service !== 'all') {
      filtered = filtered.filter(b =>
        b?.serviceName?.toLowerCase()?.includes(filters.service.replace('_', ' '))
      );
    }

    filtered.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];
      if (sortConfig?.key === 'date') {
        aValue = new Date(a.date + ' ' + (a.time || ''));
        bValue = new Date(b.date + ' ' + (b.time || ''));
      }
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredBookings(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = (query) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  };

  const handleResetFilters = () => {
    setFilters({
      searchQuery: '',
      status: 'all',
      dateRange: 'upcoming',
      service: 'all',
      startDate: '',
      endDate: ''
    });
  };

  const [showExportModal, setShowExportModal] = useState(false);

  const handleExport = () => setShowExportModal(true);

  const doExportCSV = () => {
    const csvRows = [
      ['ID', 'Client Name', 'Email', 'Phone', 'Service', 'Date', 'Time', 'Total', 'Status'],
      ...filteredBookings?.map(b => [
        b?.id,
        b?.clientName,
        b?.clientEmail,
        b?.clientPhone,
        b?.serviceName,
        b?.date,
        b?.time,
        b?.price != null ? `GH¢${Number(b.price).toFixed(2)}` : '',
        b?.status,
      ]),
    ];

    const csvContent = csvRows.map(row => row.map(escapeCsvValue).join(',')).join('\n');
    downloadTextFile({
      content: csvContent,
      fileName: `bookings-${new Date().toISOString().split('T')[0]}.csv`,
      mimeType: 'text/csv;charset=utf-8',
      includeBom: true,
    });
  };

  const doExportJSON = () => {
    const data = filteredBookings?.map(b => ({
      id: b?.id,
      clientName: b?.clientName,
      clientEmail: b?.clientEmail,
      clientPhone: b?.clientPhone,
      serviceName: b?.serviceName,
      date: b?.date,
      time: b?.time,
      total: b?.price ?? null,
      status: b?.status,
    })) ?? [];

    downloadTextFile({
      content: JSON.stringify(data, null, 2),
      fileName: `bookings-${new Date().toISOString().split('T')[0]}.json`,
      mimeType: 'application/json;charset=utf-8',
    });
  };

  const doExportPDF = () => {
    const html = generateBookingsReport(filteredBookings, filters, user?.company?.name);
    openPrintableReport(html);
  };

  const handleDoExport = (format) => {
    if (format === 'pdf') doExportPDF();
    else if (format === 'json') doExportJSON();
    else doExportCSV();
  };

  const handleSelectBooking = (id, checked) => {
    setSelectedBookings(prev =>
      checked ? [...prev, id] : prev?.filter(bookingId => bookingId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedBookings(checked ? filteredBookings?.map(b => b?.id) : []);
  };

  const handleBulkAction = async (action) => {
    const mapping = {
      confirm: 'confirmed',
      complete: 'completed',
      inprogress: 'inprogress',
      cancel: 'cancelled',
      noshow: 'noshow',
    };

    const status = mapping[action];

    if (status && selectedBookings.length > 0) {
      try {
        const result = await bulkUpdateBookingStatus(selectedBookings, status);

        if (result?.failed > 0) {
          console.warn(`${result.succeeded} updated, ${result.failed} failed`);
          // optionally surface this to the user
        }

        loadBookings(page);
      } catch (err) {
        console.error('Bulk update failed', err);
      }
    }

    setSelectedBookings([]);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      await loadBookings(page);
      setSelectedBooking(prev => prev?.id === bookingId ? { ...prev, status: newStatus } : prev);
    } catch (err) {
      console.error('Failed to change status', err);
      // as fallback update locally
      setBookings(prev => prev?.map(b =>
        b?.id === bookingId ? { ...b, status: newStatus } : b
      ));
      setSelectedBooking(prev => prev?.id === bookingId ? { ...prev, status: newStatus } : prev);
    }
  };

  const handlePaymentRecorded = async (bookingId, paymentResult) => {
    setBookings((current) => current.map((booking) => (
      booking.id === bookingId ? { ...booking, ...paymentResult } : booking
    )));
    setSelectedBooking((current) => (
      current?.id === bookingId ? { ...current, ...paymentResult } : current
    ));
    const [details] = await Promise.all([
      repositoryContainer.booking.getById(bookingId).catch(() => null),
      loadBookings(page),
    ]);
    const normalizedDetails = details?.response ?? details;
    if (normalizedDetails) setSelectedBooking(normalizedDetails);
  };

  const handleContact = (booking) => {
    window.location.href = `tel:${booking?.clientPhone}`;
  };

  const hasActiveFilters = filters?.searchQuery || filters?.status !== 'all' || 
                          filters?.dateRange !== 'upcoming' || filters?.service !== 'all';

  const allSelected = filteredBookings?.length > 0 && 
                     selectedBookings?.length === filteredBookings?.length;

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation
          userName="Admin User"
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-24">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-2">
                Bookings Management
              </h1>
              <p className="text-muted-foreground font-caption">
                {isAdmin ? 'View and manage all client appointments' : 'View all client appointments'}
              </p>
            </div>

            <BookingFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onReset={handleResetFilters}
              onExport={handleExport}
            />

            {filteredBookings?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border">
                <EmptyBookingsState
                  hasFilters={hasActiveFilters}
                  onResetFilters={handleResetFilters}
                />
              </div>
            ) : (
              <>
                <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
                  <div className="overflow-auto max-h-[calc(100vh-360px)] custom-scrollbar">
                    <table className="w-full">
                      <thead className="bg-muted border-b border-border sticky top-0 z-10">
                        <tr>
                          {isAdmin && (
                            <th className="px-4 py-4 text-left">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => handleSelectAll(e?.target?.checked)}
                                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                              />
                            </th>
                          )}
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={() => handleSort('clientName')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Client
                              <Icon
                                name={sortConfig?.key === 'clientName' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                size={16}
                              />
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Phone
                            </span>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={() => handleSort('serviceName')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Service
                              <Icon
                                name={sortConfig?.key === 'serviceName' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                size={16}
                              />
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={() => handleSort('date')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Date
                              <Icon
                                name={sortConfig?.key === 'date' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                size={16}
                              />
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Time
                            </span>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={() => handleSort('price')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Total
                              <Icon
                                name={sortConfig?.key === 'price' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                size={16}
                              />
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <button
                              onClick={() => handleSort('status')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Status
                              <Icon
                                name={sortConfig?.key === 'status' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                size={16}
                              />
                            </button>
                          </th>
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Actions
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings?.map(booking => (
                          <BookingTableRow
                            key={booking?.id}
                            booking={booking}
                            isSelected={selectedBookings?.includes(booking?.id)}
                            onSelect={handleSelectBooking}
                            canSelect={isAdmin}
                            onViewDetails={setSelectedBooking}
                            onContact={handleContact}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="lg:hidden space-y-4">
                  {filteredBookings?.map(booking => (
                    <BookingMobileCard
                      key={booking?.id}
                      booking={booking}
                      isSelected={selectedBookings?.includes(booking?.id)}
                      onSelect={handleSelectBooking}
                      canSelect={isAdmin}
                      onViewDetails={setSelectedBooking}
                      onContact={handleContact}
                    />
                  ))}
                </div>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                  isLoading={loading}
                />
              </>
            )}
          </div>
        </main>

        {isAdmin && (
          <BulkActionsBar
            selectedCount={selectedBookings?.length}
            onBulkAction={handleBulkAction}
            onClearSelection={() => setSelectedBookings([])}
          />
        )}

        {selectedBooking && (
          <BookingDetailsModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            onStatusChange={handleStatusChange}
            onPaymentRecorded={handlePaymentRecorded}
            onPriceFinalized={handlePaymentRecorded}
          />
        )}

        <ExportReportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleDoExport}
          recordCount={filteredBookings?.length ?? 0}
          title="Bookings"
          filters={filters}
        />
      </div>
    </div>
  );
};

export default BookingsManagement;
